import { FC, PropsWithChildren } from 'react'
import styled from 'styled-components'

import { media, mediaMax } from '../../styles/media'
import typeStyle from '../../styles/typeStyle'

import Container from '../layout/Container'

import Button from './Button'
import ButtonLink from './ButtonLink'
import LazyImage from './LazyImage'

const mobileHeaderHeight = 56
const desktopHeaderHeight = 100

type HeaderWrapperProps = {
  backgroundImage: string
  alignBottom?: boolean
}

const HeaderWrapper = styled.header<HeaderWrapperProps>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${300 - mobileHeaderHeight}px;
  padding: 40px 0;

  color: white;

  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  background-color: black;
  background-image: url(${({ backgroundImage }) => backgroundImage});
  ${({ alignBottom }) =>
    alignBottom &&
    `
    background-position: center 100%;
  `}

  h1 {
    font-size: 36px;
    margin-bottom: 20px;
  }

  h2 {
    font-size: 24px;
    margin-bottom: 10px;
    font-weight: 300;
  }

  @media (min-width: 768px) {
    height: 800px;

    h1 {
      font-size: 42px;
    }
    h2 {
      font-size: 30px;
    }
  }

  ${media.sm`
    height: ${500 - desktopHeaderHeight}px;
  `}

  ${media.xxxl`
    height: ${700 - desktopHeaderHeight}px;
  `}

  ${media.xwide`
    height: ${900 - desktopHeaderHeight}px;
  `}
`

type SectionProps = {
  rounded?: boolean
  grey?: boolean
  sand?: boolean
  centered?: boolean
  borderBottom?: boolean
  borderTop?: boolean
  marginBottom?: boolean
  marginTop?: boolean
}

export const Section = styled.section<SectionProps>`
  padding: 40px 0;

  ${({ rounded }) =>
    rounded &&
    `
    border-radius: 6px;
  `}

  ${({ grey }) =>
    grey &&
    `
    background: #f5f5f5;
  `}

  ${({ theme, sand }) =>
    sand &&
    `
    background: ${theme.brand.sand};
  `}

  ${({ centered }) =>
    centered &&
    `
    text-align: center;
  `}

  ${({ borderBottom }) =>
    borderBottom &&
    `
    border-bottom: 1px solid #d5d5d5;
  `}

  ${({ borderTop }) =>
    borderTop &&
    `
    border-top: 1px solid #d5d5d5;
  `}

  ${({ marginBottom }) =>
    marginBottom &&
    `
    margin-bottom: 40px;

    @media (min-width: 768px) {
      margin-bottom: 80px;
    }
  `}

${({ marginTop }) =>
    marginTop &&
    `
    margin-top: 40px;

    @media (min-width: 768px) {
      margin-top: 80px;
    }
  `}

  @media (min-width: 768px) {
    padding: 80px 0;
  }

  p {
    ${typeStyle('-ds-font-lead-sm-light')}
    margin: 0 auto;
    margin-bottom: 40px;
    max-width: 784px;

    a {
      text-decoration: none;
      color: ${({ theme }) => theme.brand.jungle};
    }
  }

  ${Button}, ${ButtonLink} {
    display: inline-block;
    width: 100%;
    max-width: 296px;
  }

  h1 {
    color: ${({ theme }) => theme.brand.forest};
  }

  h2 {
    line-height: 1.333;
  }
`

export const SectionIntro = styled.p`
  ${typeStyle('-ds-font-lead-light')}
  max-width: 1060px !important;
  margin-bottom: 0 !important;
`

type SectionRowProps = {
  reverse?: boolean
}

export const SectionRow = styled.div<SectionRowProps>`
  display: flex;
  flex-direction: ${({ reverse }) => (reverse ? 'row-reverse' : 'row')};

  & > *:not(:last-child) {
    ${({ reverse }) => (reverse ? 'margin-left: 64px;' : 'margin-right: 64px;')}
  }

  ${mediaMax.md`
    flex-direction: column;

    & > *:not(:last-child) {
      margin-right: 0;
      margin-left: 0;
      margin-bottom: 40px;
    }
  `}
`

export const SectionImage = styled(LazyImage)`
  max-width: 100%;
  min-height: 380px;

  @media (min-width: 768px) {
    margin-bottom: 0;
  }
`

type SectionHeaderProps = {
  noMargin?: boolean
}

export const SectionHeader = styled.h1<SectionHeaderProps>`
  ${typeStyle('-ds-font-title-1')}
  margin-bottom: 32px;
  color: ${({ theme }) => theme.brand.forest};

  ${({ noMargin }) =>
    noMargin &&
    `
    margin-top: 0 !important;
  `}
`

type SectionSubtitleProps = {
  noMargin?: boolean
}

export const SectionSubtitle = styled.h2<SectionSubtitleProps>`
  ${typeStyle('-ds-font-section-title-light')}
  margin-bottom: 30px;

  a {
    text-decoration: none;
    font-weight: 500;
    color: ${({ theme }) => theme.brand.jungle};
  }

  @media (min-width: 768px) {
    margin-bottom: 40px;
  }

  ${({ noMargin }) =>
    noMargin &&
    `
    margin-bottom: 0 !important;
  `}
`

export const SmallerContainer = styled(Container)`
  max-width: 1000px;
`

type LandingHeaderProps = HeaderWrapperProps & {}

export const LandingHeader: FC<PropsWithChildren<LandingHeaderProps>> = ({
  children,
  backgroundImage,
  ...custom
}) => (
  <HeaderWrapper backgroundImage={backgroundImage} {...custom}>
    <div className="container">
      <div className="row">
        <div className="col-xs-12">{children}</div>
      </div>
    </div>
  </HeaderWrapper>
)

export const ColumnReversible = styled.div.attrs({ className: 'row' })`
  ${media.lg`
    display: flex;
    align-items: center;
  `}

  ${mediaMax.md`
    display: flex;
    flex-direction: column-reverse;
    margin-top: -40px;

    img {
      margin-bottom: 33px;
    }
    .mobile-full-width {
      padding-left: 0;
      padding-right: 0;
    }
  `}
`

export const TwoButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${Button}, ${ButtonLink} {
    &:first-child {
      margin-bottom: 16px;
    }
  }

  ${media.md`
    flex-direction: row;
    ${Button}, ${ButtonLink} {
      margin: 0 8px;

      &:first-child {
        margin-bottom: 0;
      }
    }
  `}
`
